import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  AGGREGATOR_SELECTED_GROWER_EVENT,
  formatDelegationScope,
  readSelectedAggregatorGrower,
  writeSelectedAggregatorGrower,
} from "../portal/aggregatorGrowerContext.js";

export function useSelectedAggregatorGrower() {
  const [selectedGrower, setSelectedGrower] = useState(() =>
    readSelectedAggregatorGrower()
  );

  useEffect(() => {
    function syncSelectedGrower() {
      setSelectedGrower(readSelectedAggregatorGrower());
    }

    window.addEventListener(AGGREGATOR_SELECTED_GROWER_EVENT, syncSelectedGrower);
    window.addEventListener("storage", syncSelectedGrower);
    return () => {
      window.removeEventListener(
        AGGREGATOR_SELECTED_GROWER_EVENT,
        syncSelectedGrower
      );
      window.removeEventListener("storage", syncSelectedGrower);
    };
  }, []);

  return selectedGrower;
}

export function AggregatorScopeWarning({
  requiredScope,
  message = "This selected grower link does not allow this action.",
}) {
  return (
    <div className="rounded-[8px] border border-[#d8a31a]/40 bg-[#fff8e5] p-4 text-[#7a5200]">
      <p className="text-sm font-semibold uppercase tracking-[0.14em]">
        Permission Required
      </p>
      <p className="mt-1 text-sm leading-6">{message}</p>
      {requiredScope ? (
        <p className="mt-2 text-[12px] font-semibold uppercase tracking-[0.12em]">
          Needed: {formatDelegationScope(requiredScope)}
        </p>
      ) : null}
    </div>
  );
}

export default function AggregatorSelectedGrowerPanel({
  selectedGrower,
  onClear,
  compact = false,
}) {
  if (!selectedGrower) {
    return (
      <div className="rounded-[8px] border border-[#f15d22]/35 bg-[#fff3ed] p-5 text-[#9a3514]">
        <p className="text-sm font-semibold uppercase tracking-[0.14em]">
          Grower Required
        </p>
        <p className="mt-2 text-sm leading-6">
          Select an authorized grower before opening this workflow. The
          selected profile is sent with each request and still checked by the
          backend delegation rules.
        </p>
        <Link
          className="mt-4 inline-flex rounded-[4px] bg-[#243245] px-4 py-2 text-sm font-semibold text-white"
          to="/growers"
        >
          Select grower
        </Link>
      </div>
    );
  }

  return (
    <div
      className={`rounded-[8px] border border-[#a7d7b7] bg-[#eef8f0] text-[#1f6d38] ${
        compact ? "px-4 py-3" : "p-5"
      }`}
    >
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.14em]">
            Selected Grower
          </p>
          <p className={compact ? "mt-1 font-semibold" : "mt-1 text-xl font-semibold"}>
            {selectedGrower.label}
          </p>
          <p className={compact ? "mt-0.5 text-xs" : "mt-1 text-sm"}>
            {selectedGrower.grower_profile_public_id}
          </p>
          {Array.isArray(selectedGrower.scope) && selectedGrower.scope.length > 0 ? (
            <div className={compact ? "mt-2 flex flex-wrap gap-2" : "mt-3 flex flex-wrap gap-2"}>
              {selectedGrower.scope.map((scope) => (
                <span
                  className="rounded-[4px] border border-[#9db8a7] bg-white/60 px-2.5 py-1 text-[12px] font-semibold"
                  key={scope}
                >
                  {formatDelegationScope(scope)}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            className={`rounded-[4px] border border-[#9db8a7] text-sm font-semibold ${
              compact ? "px-3 py-1.5" : "px-4 py-2"
            }`}
            to="/growers"
          >
            Change
          </Link>
          {onClear ? (
            <button
              className="rounded-[4px] border border-[#9db8a7] px-4 py-2 text-sm font-semibold"
              onClick={() => {
                writeSelectedAggregatorGrower(null);
                onClear();
              }}
              type="button"
            >
              Clear
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
